import { useState, useRef } from 'react';
import { useSnackbar } from '../../../components/common/Snackbar';
import { MAX_TOTAL_SIZE, ALLOWED_FILE_TYPES, EXTENSION_MIME_FALLBACK, ALLOWED_FILE_TYPES_LABEL } from '../../../constants/raiseQueryForm';

/**
 * Hook to manage file attachments for RaiseQueryForm
 * @param {Object} fileInputRef - Ref for file input element
 * @returns {Object} Attachments state and handlers
 */
export const useRaiseQueryAttachments = (fileInputRef) => {
  const { showSnackbar } = useSnackbar();
  
  const [attachments, setAttachments] = useState([]);
  const [attachmentInputKey, setAttachmentInputKey] = useState(Date.now());
  const [isDragging, setIsDragging] = useState(false);

  const isValidFileType = (file) => {
    // Check by MIME type first
    if (ALLOWED_FILE_TYPES.includes(file.type)) return true;

    // Fallback: check by file extension (browsers may report octet-stream for .msg, .docx, etc.)
    const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    return ext in EXTENSION_MIME_FALLBACK;
  };

  const validateFiles = (files, currentAttachments) => {
    const validFiles = [];
    const errors = [];
    
    // Calculate current total size
    const currentTotalSize = currentAttachments.reduce((sum, file) => sum + file.size, 0);

    files.forEach((file) => {
      if (!isValidFileType(file)) {
        errors.push(`${file.name}: Only ${ALLOWED_FILE_TYPES_LABEL} files are allowed`);
        return;
      }

      // Check if adding this file would exceed total size limit
      const newTotalSize = currentTotalSize + validFiles.reduce((sum, f) => sum + f.size, 0) + file.size;
      if (newTotalSize > MAX_TOTAL_SIZE) {
        errors.push(`Total file size exceeds ${MAX_TOTAL_SIZE / (1024 * 1024)}MB limit (all files combined)`);
        return;
      }

      validFiles.push(file);
    });

    return { validFiles, errors };
  };

  const handleAttachmentChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    const { validFiles, errors } = validateFiles(files, attachments);

    if (errors.length > 0) {
      showSnackbar(errors.join('. '), 'error', { duration: 5000 });
    }

    if (validFiles.length > 0) {
      setAttachments((prev) => [...prev, ...validFiles]);
      setAttachmentInputKey(Date.now());
    }
  };

  const handleRemoveAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDropZoneClick = () => {
    fileInputRef.current?.click();
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isDragging) {
      setIsDragging(true);
    }
    event.dataTransfer.dropEffect = 'copy';
  };

  const handleDragEnter = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const related = event.relatedTarget;
    if (!event.currentTarget.contains(related)) {
      setIsDragging(false);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    const files = Array.from(event.dataTransfer?.files || []);
    if (files.length === 0) return;

    const { validFiles, errors } = validateFiles(files, attachments);

    if (errors.length > 0) {
      showSnackbar(errors.join('. '), 'error', { duration: 5000 });
    }

    if (validFiles.length > 0) {
      setAttachments((prev) => [...prev, ...validFiles]);
      setAttachmentInputKey(Date.now());
    }

    event.dataTransfer.clearData();
  };

  const resetAttachments = () => {
    setAttachments([]);
    setAttachmentInputKey(Date.now());
  };

  return {
    attachments,
    setAttachments,
    attachmentInputKey,
    isDragging,
    handleAttachmentChange,
    handleRemoveAttachment,
    handleDropZoneClick,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
    resetAttachments,
  };
};
